import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode } from 'react';
import { forwardRef, useId } from 'react';
import { AlertTriangle, Inbox, type LucideIcon } from 'lucide-react';
import { cx } from '../lib/cx';

/**
 * Os blocos da interface.
 *
 * Poucos e genéricos de propósito: um design system completo para seis telas
 * seria mais código para manter do que tela para mostrar. O que existe aqui é o
 * que aparece em mais de um lugar — e cada peça carrega a acessibilidade junto,
 * porque acessibilidade adicionada depois vira uma lista de pendências que
 * ninguém fecha.
 */

// =============================================================== botões ====

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  loading?: boolean;
  children: ReactNode;
}

/**
 * O tratamento de CTA — caixa alta, `font-black`, `tracking-widest` — fica na
 * primária e na destrutiva, e só nelas.
 *
 * É a assinatura do StackPlus, e ela funciona porque é rara: aplicada também em
 * "Voltar" e "Cancelar", uma tela de listagem com seis botões vira seis
 * chamadas para ação e nenhuma hierarquia. Secundário e fantasma ficam em caixa
 * normal, que é o que os faz recuar.
 */
const CTA = 'font-black uppercase tracking-widest';

const VARIANTS: Record<ButtonVariant, string> = {
  primary: `bg-brand text-brand-fg shadow-card hover:bg-brand-secondary ${CTA}`,
  secondary: 'bg-sx-card2 text-content border border-sx-border2 hover:border-brand/60',
  ghost: 'bg-transparent text-content-muted hover:bg-sx-card2 hover:text-content',
  danger: `bg-sx-danger text-sx-bg shadow-card hover:brightness-110 ${CTA}`,
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', fullWidth, loading, disabled, className, children, ...props },
  ref,
) {
  return (
    <button
      ref={ref}
      // Um botão em carregamento continua FOCÁVEL e anuncia o estado: trocar o
      // texto por um spinner sem avisar deixa quem usa leitor de tela sem saber
      // se o toque funcionou.
      aria-busy={loading || undefined}
      disabled={disabled || loading}
      className={cx(
        'tap-target inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3',
        'text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-45',
        VARIANTS[variant],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading && <Spinner className="h-4 w-4" />}
      {children}
    </button>
  );
});

// ================================================================ campos ====

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: string;
  error?: string | null;
}

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { label, hint, error, className, id, ...props },
  ref,
) {
  const gerado = useId();
  const inputId = id ?? gerado;
  const descricaoId = `${inputId}-descricao`;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={inputId} className="text-sm font-medium text-content">
        {label}
      </label>

      <input
        ref={ref}
        id={inputId}
        // O erro é ligado ao campo, e não só pintado de vermelho: sem
        // `aria-describedby`, um leitor de tela anuncia "campo inválido" sem
        // dizer o que houve.
        aria-invalid={error ? true : undefined}
        aria-describedby={error || hint ? descricaoId : undefined}
        className={cx(
          // O fundo do campo é mais escuro que o do card (#202020 sobre
          // #232323) — sobre near-black, um campo da mesma cor da superfície
          // some, e o usuário não enxerga onde escrever.
          'tap-target rounded-xl border bg-sx-input px-4 py-3 text-base text-content',
          'placeholder:text-sx-faint',
          error ? 'border-sx-danger' : 'border-sx-border',
          className,
        )}
        {...props}
      />

      {(error || hint) && (
        <p
          id={descricaoId}
          className={cx('text-xs', error ? 'text-sx-danger' : 'text-content-muted')}
          role={error ? 'alert' : undefined}
        >
          {error ?? hint}
        </p>
      )}
    </div>
  );
});

// ============================================================== estrutura ====

export function Card({
  children,
  className,
  as: Tag = 'div',
}: {
  children: ReactNode;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'li';
}) {
  return (
    <Tag className={cx('rounded-card border border-border bg-surface p-4 shadow-card', className)}>
      {children}
    </Tag>
  );
}

export function Badge({
  children,
  tone = 'neutral',
}: {
  children: ReactNode;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | 'brand';
}) {
  // Sobre near-black, a etiqueta é o WASH da cor com o texto na cor cheia. O
  // inverso (fundo cheio) daria cinco manchas saturadas numa linha de tabela.
  const tones = {
    neutral: 'bg-sx-card2 text-content-muted',
    success: 'bg-sx-success/10 text-sx-success',
    warning: 'bg-sx-warn/10 text-sx-warn',
    danger: 'bg-sx-danger/10 text-sx-danger',
    brand: 'bg-brand/10 text-brand',
  } as const;

  return (
    <span className={cx('rounded-full px-2.5 py-1 text-xs font-medium', tones[tone])}>
      {children}
    </span>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-content-muted">
        {children}
      </h2>
      {action}
    </div>
  );
}

// ================================================================ estados ====

export function Spinner({ className }: { className?: string }) {
  return (
    <svg
      className={cx('animate-spin', className ?? 'h-5 w-5')}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

/**
 * Espera com forma.
 *
 * Um esqueleto com o tamanho do conteúdo que vem evita o salto de layout que
 * faz o dedo tocar no lugar errado quando a lista aparece.
 */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx('animate-pulse rounded-xl bg-sx-card2', className ?? 'h-20 w-full')}
      aria-hidden="true"
    />
  );
}

export function LoadingState({ label = 'Carregando…' }: { label?: string }) {
  return (
    <div className="flex flex-col items-center gap-3 py-12 text-content-muted" role="status">
      <Spinner className="h-6 w-6" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  // Ícone como COMPONENTE, e não como string de emoji: o emoji é renderizado
  // pela fonte do sistema, então o mesmo caractere sai colorido no Android,
  // achatado no Windows e ausente em alguns Linux. Um SVG é o mesmo desenho em
  // todo lugar, e herda a cor do texto.
  icon: Icon = Inbox,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
  icon?: LucideIcon;
}) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-card border border-dashed border-border px-6 py-12 text-center">
      <Icon className="h-8 w-8 text-sx-faint" aria-hidden="true" strokeWidth={1.5} />
      <p className="font-semibold text-content">{title}</p>
      {description && <p className="max-w-xs text-sm text-content-muted">{description}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/**
 * Falha com saída.
 *
 * Todo erro mostrado ao cliente traz o que fazer a seguir. Uma tela que só diz
 * "erro ao carregar" transfere para ele um problema que ele não tem como
 * resolver.
 */
export function ErrorState({
  title = 'Não foi possível carregar',
  description,
  onRetry,
}: {
  title?: string;
  description?: string;
  onRetry?: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex flex-col items-center gap-3 rounded-card border border-sx-danger/40 bg-sx-danger/10 px-6 py-10 text-center"
    >
      <AlertTriangle className="h-8 w-8 text-sx-danger" aria-hidden="true" strokeWidth={1.5} />
      <p className="font-semibold text-content">{title}</p>
      {description && <p className="max-w-sm text-sm text-content-muted">{description}</p>}
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Tentar de novo
        </Button>
      )}
    </div>
  );
}

/** Confirmação e falha de uma AÇÃO, sem tirar o cliente da tela. */
export function Feedback({ tone, children }: { tone: 'success' | 'error'; children: ReactNode }) {
  return (
    <p
      role={tone === 'error' ? 'alert' : 'status'}
      className={cx(
        'rounded-xl border px-4 py-3 text-sm',
        tone === 'success'
          ? 'border-sx-success/40 bg-sx-success/10 text-sx-success'
          : 'border-sx-danger/40 bg-sx-danger/10 text-sx-danger',
      )}
    >
      {children}
    </p>
  );
}
